import { gql, useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useFeatureFlag } from "shared/contextProviders/featureFlags";
import { FeatureFlags } from "shared/featureFlags";

const ORG_TEST_DATA_QUERY = gql`
  query OrgTestData($organizationId: String!) {
    testRecordsByOrganization(organizationId: $organizationId) {
      count
      testTagId
      testOperatorEntityIds
    }
  }
`;

type OrgTestDataResult = {
  testRecordsByOrganization: {
    count: number;
    testTagId?: string | null;
    testOperatorEntityIds: string[];
  };
};

/**
 * Test Data summary for an organization. Returns empty values for users who
 * can't manage Test Data (the query is admin-only and errors are ignored).
 */
export const useOrgTestData = (organizationId?: string | null, skip = false) => {
  const enabled = useFeatureFlag(FeatureFlags.OnboardingTestDataTool);
  const { data } = useQuery<OrgTestDataResult>(ORG_TEST_DATA_QUERY, {
    variables: { organizationId: organizationId ?? "" },
    skip: skip || !enabled || !organizationId,
    errorPolicy: "ignore",
    fetchPolicy: "cache-and-network",
  });
  const summary = enabled ? data?.testRecordsByOrganization : undefined;
  const testOperatorEntityIds = useMemo(
    () => new Set(summary?.testOperatorEntityIds ?? []),
    [summary?.testOperatorEntityIds],
  );
  return {
    testUserCount: summary?.count ?? 0,
    testTagId: summary?.testTagId ?? "",
    testOperatorEntityIds,
  };
};
